import dot from 'dot-prop-immutable';
import { denormalize } from 'normalizr';
import reduceReducers from 'reduce-reducers';

import { createEntitiesReducer } from 'modules/common/redux';
import { FORMULA_COMPILE_SUCCESS_MSG } from 'modules/constants/common/all';

import { formatTimestamp } from 'utils/dateUtils';

import { types } from './actions';
import { profitItemDefSchema, variableSchema } from './entities';

const initialState = {
  profitItemId: null,
  entities: {},
  formulaEditor: {
    isModalOpen: false,
    isUpdating: false,
    isCompiling: false,
    message: null,
    error: false,
  },
  isDeletingProfitItem: false,
};

const entitiesReducer = createEntitiesReducer(
  ['profitItemDefs', 'variables'],
  [
    types.RECEIVE_PROFIT_ITEM,
    types.UPDATE_PROFIT_ITEM_SUCCESS,
    types.UPDATE_VARIABLE_SUCCESS,
  ],
);

const reducer = (state, action) => {
  const { payload, type } = action;
  switch (type) {
    case types.DELETE_PROFIT_ITEM_ERROR:
    case types.DELETE_PROFIT_ITEM_SUCCESS:
      return {
        ...state,
        isDeletingProfitItem: false,
      };
    case types.DELETE_PROFIT_ITEM_REQUEST:
      return {
        ...state,
        isDeletingProfitItem: true,
      };
    case types.FORMULA_OPEN_MODAL:
      return dot.merge(state, 'formulaEditor', {
        isModalOpen: true,
      });
    case types.FORMULA_CLOSE_MODAL:
      return dot.merge(state, 'formulaEditor', {
        isModalOpen: false,
        message: null,
        error: false,
      });
    case types.FORMULA_COMPILE_REQUEST:
      return dot.merge(state, 'formulaEditor', {
        isCompiling: true,
      });
    case types.FORMULA_COMPILE_SUCCESS:
      return dot.merge(state, 'formulaEditor', {
        isCompiling: false,
        message: FORMULA_COMPILE_SUCCESS_MSG,
        error: false,
      });
    case types.FORMULA_COMPILE_ERROR:
      return dot.merge(state, 'formulaEditor', {
        isCompiling: false,
        message: action.payload.error,
        error: true,
      });
    case types.FORMULA_UPDATE_REQUEST:
      return dot.merge(state, 'formulaEditor', {
        isUpdating: true,
      });
    case types.FORMULA_UPDATE_SUCCESS: {
      let nextState = state;
      nextState = dot.merge(
        nextState,
        `entities.processes.${action.payload.processId}.opFactoryFormula`,
        action.payload.formula,
      );
      const savedMsg = action.payload.formula.revertedFrom
        ? `Reverted from: ${formatTimestamp(
            action.payload.formula.revertedFrom,
          )}`
        : 'Saved';
      nextState = dot.merge(nextState, 'formulaEditor', {
        isUpdating: false,
        error: false,
        message: savedMsg,
      });
      return nextState;
    }
    case types.FORMULA_UPDATE_ERROR:
      return dot.merge(state, 'formulaEditor', {
        isUpdating: false,
        message: action.payload.error,
        error: true,
      });
    case types.FORMULA_RESET_MESSAGE:
      return dot.merge(state, 'formulaEditor', {
        message: '',
        error: false,
      });
    case types.RECEIVE_PROFIT_ITEM:
      return {
        ...state,
        profitItemId: payload.result,
      };
    default:
      return state;
  }
};

export default reduceReducers(entitiesReducer, reducer, initialState);

const getScope = (state) => state.modules.profitItems.detailsPage;

export const getProfitItemId = (state) => getScope(state).profitItemId;

export const getProfitItem = (state) =>
  denormalize(
    getProfitItemId(state),
    profitItemDefSchema,
    getScope(state).entities,
  );

export const getFormulaEditorState = (state) => getScope(state).formulaEditor;

export const getVariable = (state, variableId) =>
  denormalize(variableId, variableSchema, getScope(state).entities);

export const isDeletingProfitItem = (state) =>
  getScope(state).isDeletingProfitItem;
